import type { InputRef } from "antd";
import { debounce } from "lodash-es";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DDRendererItem, DDRendererProps } from "../types";

export const useDefaultDDRenderer = <ItemMetadata>({
  onSearch,
  items,
  onItemSelect,
  debounceTimeInMs = 200,
  noSearch,
}: DDRendererProps<ItemMetadata>) => {
  const inputRef = useRef<InputRef>(null);

  const [localItems, setLocalItems] = useState<DDRendererItem<ItemMetadata>[]>(
    [],
  );
  const [inputValue, setInputValue] = useState("");

  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const commandListRef = useRef<HTMLDivElement>(null);
  const [selectedValue, setSelectedValue] = useState<string>("");

  // Set initial selection when items change
  useEffect(() => {
    if (localItems?.length > 0) {
      setSelectedValue(localItems[0].id.toString());
    }
  }, [localItems]);

  /**
   * Update the local items when the items prop changes
   */
  useEffect(() => {
    if (items) {
      setLocalItems(items);
    }
  }, [items]);

  /**
   * Debounce the search options
   * @param searchText string
   */
  const handleSearchOptions = useMemo(
    () =>
      debounce((searchText: string) => {
        if (typeof onSearch === "function") {
          onSearch(searchText);
        }

        if (searchText.trim().length === 0) {
          setLocalItems(items || []);
        } else {
          const filteredItems = items?.filter(item => {
            if (typeof item.label === "string") {
              return item.label
                .toLowerCase()
                .includes(searchText.toLowerCase());
            } else {
              return item.searchableText
                ?.toLowerCase()
                .includes(searchText.toLowerCase());
            }
          });

          setLocalItems(filteredItems);
        }
      }, debounceTimeInMs),
    [items, onSearch, debounceTimeInMs],
  );

  /**
   * Cancel the debounce when the component is unmounted
   */
  useEffect(() => {
    return () => {
      handleSearchOptions.cancel();
    };
  }, [handleSearchOptions]);

  useEffect(() => {
    if (inputRef.current && !noSearch) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef.current]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isScrollable = target.scrollHeight > target.clientHeight;
    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 30; // 30px threshold

    setShowScrollIndicator(isScrollable && !isNearBottom);
  };

  // Check for scrollable content on mount and when items change
  useEffect(() => {
    const checkScrollable = () => {
      if (commandListRef.current) {
        const isScrollable =
          commandListRef.current.scrollHeight >
          commandListRef.current.clientHeight;
        const isNearBottom =
          commandListRef.current.scrollHeight -
            commandListRef.current.scrollTop -
            commandListRef.current.clientHeight <
          30;

        setShowScrollIndicator(isScrollable && !isNearBottom);
      }
    };

    checkScrollable();
    // Add resize observer to check when content size changes
    const resizeObserver = new ResizeObserver(checkScrollable);
    if (commandListRef.current) {
      resizeObserver.observe(commandListRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [localItems]); // Re-run when items change

  const handleInputChange = (inputEvent: ChangeEvent<HTMLInputElement>) => {
    const searchText = inputEvent.target.value;
    setInputValue(searchText);
    if (searchText.trim().length > 0 || typeof onSearch === "function") {
      handleSearchOptions(searchText);
    } else {
      setLocalItems(items);
    }

    if (items?.length > 0) {
      setSelectedValue(items[0].id.toString());
    }
  };

  const handleItemSelect = useCallback(
    (item: DDRendererItem<ItemMetadata>) => {
      if (typeof onItemSelect === "function") {
        onItemSelect(item);
      }
    },
    [onItemSelect],
  );

  return {
    inputRef,
    inputValue,
    localItems,
    handleInputChange,
    handleItemSelect,
    showScrollIndicator,
    commandListRef,
    handleScroll,
    selectedValue,
    setSelectedValue,
  };
};
